import { documentNatureLabel } from "@etudescarre/constants";
import { cn } from "@etudescarre/ui";
import { isNil, isNotNil } from "@etudescarre/utils";
import {
  CaretDown as CaretDownIcon,
  CaretRight as CaretRightIcon,
  ListChecks as ListChecksIcon,
  PaperPlaneTilt as PaperPlaneIcon,
} from "@phosphor-icons/react";
import { Fragment, useState } from "react";
import {
  stickyRowHeaderContentClass,
  useStickyHeaderOffsets,
} from "@/lib/utils/sticky-table-header";
import { MaquetteFormatButtons } from "../reception-sas/maquettes-table";
import { AwaitingDiffusionLabel } from "./diffusion-dialog";
import {
  NOTICE_FLAG_CLASSES,
  NOTICE_FLAG_LABELS,
  NoticeFlagSelect,
} from "./notice-flag-select";
import { SyntheseCell } from "./synthese-cell";
import type { DocumentData, LotData, MaquetteData, VersionData } from "./types";
import { VersionCell } from "./version-cell";
import { isVersionEstompee, isVersionVisaEnRetard } from "./visa-retard";

export type DocumentTableProps = {
  lots: LotData[];
  canMarkBpe: boolean;
  /** Sélection du filtre "Documents diffusés à" (ids d'équipes). */
  selectedDiffuseATeamIds?: number[];
  onViewPdf: (fichierPdfId: number) => void;
  onDownloadNative: (fichierNativeId: number) => void;
  onMarkBpe: (versionId: number, documentId: number) => void;
  onOpenSynthese: (versionId: number) => void;
  onOpenDestinataires: (
    versionId: number,
    type: "avis" | "coordination",
  ) => void;
  /** Ouvre la grille de transmission interne de la version. */
  onOpenInterne: (versionId: number, documentId: number) => void;
  onOpenDiffusion: (versionId: number, documentId: number) => void;
  showDiffusionActions?: boolean;
  /**
   * Colonne « Consigne mise à jour document » (écrans 015 et 035). Filled on
   * the documents whose `consignesVisible` is set.
   */
  showNoticeFlag?: boolean;
  /**
   * Makes the instruction of the documents the user pilots editable (N93).
   * Without it, the column is read only.
   */
  noticeFlagEditor?: { yardId: string; onChanged: () => void };
  /**
   * Opens the consignes recap of a version (N8). With `showNoticeFlag`, adds
   * the check-list picto on each index row of the instruction column.
   */
  onOpenRecap?: (versionId: number, documentId: number) => void;
  /**
   * Opens the IFC viewer of a document's source maquette. When set, the table
   * shows the BIM column (screen 015).
   */
  onViewIfc?: (fichierIfcId: number, maquetteTitle: string) => void;
  /** Filters are active: an empty list says so, not « no document yet ». */
  filtered?: boolean;
  /**
   * The table sits in a list that scrolls (zone detail). It grows with its rows
   * and its header pins to that list, instead of scrolling on its own.
   */
  inScrollingList?: boolean;
};

export function DocumentTable({
  lots,
  canMarkBpe,
  selectedDiffuseATeamIds = [],
  onViewPdf,
  onDownloadNative,
  onMarkBpe,
  onOpenSynthese,
  onOpenDestinataires,
  onOpenInterne,
  onOpenDiffusion,
  showDiffusionActions = true,
  showNoticeFlag = false,
  noticeFlagEditor,
  onOpenRecap,
  onViewIfc,
  filtered = false,
  inScrollingList = false,
}: DocumentTableProps) {
  // Lots open by default, including those a filter brings back later.
  const [collapsedLots, setCollapsedLots] = useState<Set<number>>(
    () => new Set(),
  );
  const { headerRef, style: stickyStyle } = useStickyHeaderOffsets();

  if (lots.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center py-16 text-center">
        <p className="text-zinc-500 dark:text-zinc-400">
          {filtered
            ? "Aucun document ne correspond aux filtres"
            : "Aucun document d'exécution pour ce chantier"}
        </p>
        <p className="mt-1 text-sm text-zinc-400 dark:text-zinc-500">
          {filtered
            ? "Modifiez les filtres ou cliquez sur « Effacer »."
            : "Les documents apparaîtront ici une fois enregistrés."}
        </p>
      </div>
    );
  }

  const toggleLot = (lotId: number) => {
    setCollapsedLots((prev) => {
      const next = new Set(prev);
      if (next.has(lotId)) {
        next.delete(lotId);
      } else {
        next.add(lotId);
      }
      return next;
    });
  };

  const showRecap = showNoticeFlag && isNotNil(onOpenRecap);
  // expand, Auteur, N°, Intitulé, Nature, Zones, Ind., Formats, Synthèse
  const totalCols =
    9 +
    (isNotNil(onViewIfc) ? 1 : 0) +
    (showNoticeFlag ? 1 : 0) +
    (showRecap ? 1 : 0);

  return (
    <div
      className={cn(
        "rounded-lg border border-zinc-200 dark:border-zinc-700",
        !inScrollingList && "min-h-0 flex-1 overflow-auto",
      )}
      style={stickyStyle}
    >
      <table className="w-full min-w-[60rem] table-fixed border-collapse text-sm">
        {/* Variable columns in plain percentages: Chrome and WebKit ignore
            calc() widths on <col>. */}
        <colgroup>
          <col className="w-6" />
          <col className="w-[11%]" />
          <col className="w-[9%]" />
          <col className="w-[15%]" />
          <col className="w-27" />
          <col className="w-[8%]" />
          <col className="w-14" />
          <col className="w-24" />
          {isNotNil(onViewIfc) && <col className="w-16" />}
          {showRecap && <col className="w-10" />}
          {showNoticeFlag && <col className="w-27" />}
          <col className="w-34" />
        </colgroup>
        <thead ref={headerRef}>
          <tr className="border-b border-zinc-200 dark:border-zinc-700">
            <th className={headCellClass} />
            <th className={cn(headCellClass, "px-3 text-left")}>Auteur</th>
            <th className={cn(headCellClass, "px-3 text-left")}>N°</th>
            <th className={cn(headCellClass, "px-3 text-left")}>Intitulé</th>
            <th className={cn(headCellClass, "px-3 text-left")}>Nature</th>
            <th className={cn(headCellClass, "px-3 text-left")}>Zones</th>
            <th className={headCellClass}>Ind.</th>
            <th className={headCellClass}>Formats</th>
            {isNotNil(onViewIfc) && <th className={headCellClass}>BIM</th>}
            {showNoticeFlag && (
              <th className={headCellClass} colSpan={showRecap ? 2 : 1}>
                Consigne mise à jour document
              </th>
            )}
            <th className={headCellClass}>Synthèse</th>
          </tr>
        </thead>

        <tbody>
          {lots.map((lot) => (
            <Fragment key={lot.id}>
              {/* Lot header row */}
              <tr className="bg-zinc-100 dark:bg-zinc-800/50 border-b border-zinc-200 dark:border-zinc-700">
                <td className="px-2 py-2">
                  <button
                    type="button"
                    onClick={() => toggleLot(lot.id)}
                    className="text-zinc-500 hover:text-zinc-700 dark:text-zinc-400 dark:hover:text-zinc-200"
                  >
                    {collapsedLots.has(lot.id) ? (
                      <CaretRightIcon size={16} weight="bold" />
                    ) : (
                      <CaretDownIcon size={16} weight="bold" />
                    )}
                  </button>
                </td>
                <td
                  colSpan={totalCols - 1}
                  className="px-3 py-2 font-semibold text-zinc-800 dark:text-zinc-200"
                >
                  {lot.nLot ? `Lot ${lot.nLot} - ` : ""}
                  {lot.name}
                </td>
              </tr>

              {/* Presta groups */}
              {!collapsedLots.has(lot.id) &&
                lot.prestas.map((presta) => (
                  <Fragment key={presta.id}>
                    {/* Presta header */}
                    <tr className="bg-zinc-50/50 dark:bg-zinc-800/30 border-b border-zinc-100 dark:border-zinc-700/50">
                      <td />
                      <td
                        colSpan={totalCols - 1}
                        className="px-3 py-1.5 text-xs font-medium text-zinc-600 dark:text-zinc-400 uppercase tracking-wide"
                      >
                        {presta.name}
                      </td>
                    </tr>

                    {/* Document rows, the author written once per group */}
                    {presta.documents.map((doc, docIndex) => (
                      <DocumentRows
                        key={doc.id}
                        doc={doc}
                        authorRowSpan={authorGroupRowSpan(
                          presta.documents,
                          docIndex,
                        )}
                        canMarkBpe={canMarkBpe}
                        selectedDiffuseATeamIds={selectedDiffuseATeamIds}
                        onViewPdf={onViewPdf}
                        onDownloadNative={onDownloadNative}
                        onMarkBpe={onMarkBpe}
                        onOpenSynthese={onOpenSynthese}
                        onOpenDestinataires={onOpenDestinataires}
                        onOpenInterne={onOpenInterne}
                        onOpenDiffusion={onOpenDiffusion}
                        showDiffusionActions={showDiffusionActions}
                        showNoticeFlag={showNoticeFlag}
                        noticeFlagEditor={noticeFlagEditor}
                        onOpenRecap={showRecap ? onOpenRecap : undefined}
                        onViewIfc={onViewIfc}
                      />
                    ))}
                  </Fragment>
                ))}
            </Fragment>
          ))}
        </tbody>
      </table>
    </div>
  );
}

// Safari ignores `position: sticky` on <thead>/<tr>: pin each header cell.
const headCellClass =
  "sticky top-0 z-10 bg-zinc-50 px-2 py-2 text-center font-medium text-zinc-700 dark:bg-zinc-800 dark:text-zinc-300";

/** Rows of a version-per-row document: one per version, at least one. */
function documentRowCount(doc: DocumentData): number {
  return Math.max(doc.versions.length, 1);
}

/**
 * Rows the author cell spans when `documents[docIndex]` opens a run of
 * documents by the same author (the API sorts them by author), 0 otherwise.
 */
function authorGroupRowSpan(
  documents: DocumentData[],
  docIndex: number,
): number {
  const author = documents[docIndex].authorCompanyName;
  if (documents[docIndex - 1]?.authorCompanyName === author) return 0;
  let rowSpan = 0;
  for (const doc of documents.slice(docIndex)) {
    if (doc.authorCompanyName !== author) break;
    rowSpan += documentRowCount(doc);
  }
  return rowSpan;
}

type DocumentRowsProps = {
  doc: DocumentData;
  /** See authorGroupRowSpan: the author cell is rendered when positive. */
  authorRowSpan: number;
  canMarkBpe: boolean;
  selectedDiffuseATeamIds: number[];
  onViewPdf: (fichierPdfId: number) => void;
  onDownloadNative: (fichierNativeId: number) => void;
  onMarkBpe: (versionId: number, documentId: number) => void;
  onOpenSynthese: (versionId: number) => void;
  onOpenDestinataires: (
    versionId: number,
    type: "avis" | "coordination",
  ) => void;
  onOpenInterne: (versionId: number, documentId: number) => void;
  onOpenDiffusion: (versionId: number, documentId: number) => void;
  showDiffusionActions: boolean;
  showNoticeFlag: boolean;
  noticeFlagEditor?: DocumentTableProps["noticeFlagEditor"];
  onOpenRecap?: DocumentTableProps["onOpenRecap"];
  onViewIfc?: (fichierIfcId: number, maquetteTitle: string) => void;
};

/**
 * One row per version, latest index on top as in the screen mockups. The
 * document cells and its update instruction span them all.
 */
function DocumentRows({
  doc,
  authorRowSpan,
  canMarkBpe,
  selectedDiffuseATeamIds,
  onViewPdf,
  onDownloadNative,
  onMarkBpe,
  onOpenSynthese,
  onOpenDestinataires,
  onOpenInterne,
  onOpenDiffusion,
  showDiffusionActions,
  showNoticeFlag,
  noticeFlagEditor,
  onOpenRecap,
  onViewIfc,
}: DocumentRowsProps) {
  // The API sends versions oldest first.
  const rows: (VersionData | null)[] =
    doc.versions.length > 0 ? [...doc.versions].reverse() : [null];
  const docCellClass = "text-xs text-zinc-600 dark:text-zinc-400";

  return rows.map((version, rowIndex) => {
    const faded =
      isNotNil(version) && isVersionEstompee(version, selectedDiffuseATeamIds);
    const versionCellClass = cn("px-2 text-center", faded && "opacity-40");
    const maquette = version?.maquette;

    return (
      <tr
        key={version?.id ?? "empty"}
        className="h-9 border-b border-zinc-100 dark:border-zinc-700/50 hover:bg-zinc-50/50 dark:hover:bg-zinc-800/20"
      >
        {rowIndex === 0 && (
          <>
            <td rowSpan={rows.length} />
            {authorRowSpan > 0 && (
              <DocumentTextCell
                rowSpan={authorRowSpan}
                className={docCellClass}
                value={doc.authorCompanyName || "-"}
              />
            )}
            <DocumentTextCell
              rowSpan={rows.length}
              className="font-mono text-xs text-zinc-900 dark:text-zinc-100"
              value={doc.number}
            />
            <DocumentTextCell
              rowSpan={rows.length}
              className="text-zinc-800 dark:text-zinc-200"
              value={doc.name}
            />
            <DocumentTextCell
              rowSpan={rows.length}
              className={docCellClass}
              value={
                isNotNil(doc.nature) ? documentNatureLabel(doc.nature) : "-"
              }
            />
            <DocumentTextCell
              rowSpan={rows.length}
              className={docCellClass}
              value={doc.zones.join(", ") || "-"}
            />
          </>
        )}

        <td
          className={cn(
            versionCellClass,
            "text-xs font-medium text-zinc-700 dark:text-zinc-300",
          )}
        >
          {version?.index ?? "-"}
          {isNotNil(version) && <VersionStateLabel version={version} />}
        </td>

        <td className={versionCellClass}>
          {isNotNil(version) ? (
            <div className="flex items-center justify-center gap-1.5">
              <VersionCell
                version={version}
                canMarkBpe={canMarkBpe}
                onViewPdf={onViewPdf}
                onDownloadNative={onDownloadNative}
                onMarkBpe={(vId) => onMarkBpe(vId, doc.id)}
              />
              {canMarkBpe && showDiffusionActions && (
                <button
                  type="button"
                  onClick={() => onOpenDiffusion(version.id, doc.id)}
                  className="text-zinc-400 hover:text-blue-600 dark:hover:text-blue-400"
                  title="Diffuser"
                >
                  <PaperPlaneIcon size={14} />
                </button>
              )}
            </div>
          ) : (
            <span className="text-zinc-300 dark:text-zinc-600">-</span>
          )}
        </td>

        {isNotNil(onViewIfc) && (
          <td className={versionCellClass}>
            {isNotNil(maquette) ? (
              <MaquetteFormatButtons
                fichierIfcId={maquette.fichierIfcId}
                fichierNativeId={maquette.fichierNativeId}
                ifcTitle={`Voir la maquette IFC ${maquetteLabel(maquette)}`}
                nativeTitle={`Télécharger le fichier natif de la maquette ${maquetteLabel(maquette)}`}
                onDownload={onDownloadNative}
                onOpenIfc={(fichierIfcId) =>
                  onViewIfc(fichierIfcId, maquetteVersionLabel(maquette))
                }
              />
            ) : (
              <span className="text-zinc-300 dark:text-zinc-600">-</span>
            )}
          </td>
        )}

        {isNotNil(onOpenRecap) && (
          <td className="px-1 text-center">
            {isNotNil(version) && doc.consignesVisible && (
              <button
                type="button"
                onClick={() => onOpenRecap(version.id, doc.id)}
                className="text-zinc-500 hover:text-blue-600 dark:text-zinc-400 dark:hover:text-blue-400"
                title="Récap des consignes"
                aria-label={`Récap des consignes ${doc.number} indice ${version.index ?? "-"}`}
              >
                <ListChecksIcon size={18} />
              </button>
            )}
          </td>
        )}

        {showNoticeFlag && rowIndex === 0 && (
          <td rowSpan={rows.length} className="px-0.5 text-center">
            {doc.piloted === true && isNotNil(noticeFlagEditor) ? (
              <NoticeFlagSelect
                yardId={noticeFlagEditor.yardId}
                documentId={doc.id}
                documentLabel={`${doc.number} - ${doc.name}`}
                value={doc.noticeFlag ?? "new"}
                onChanged={noticeFlagEditor.onChanged}
              />
            ) : (
              doc.consignesVisible &&
              isNotNil(doc.noticeFlag) && (
                <span
                  className={cn(
                    "inline-block whitespace-nowrap rounded px-2 py-0.5 text-xs",
                    NOTICE_FLAG_CLASSES[doc.noticeFlag],
                  )}
                >
                  {NOTICE_FLAG_LABELS[doc.noticeFlag]}
                </span>
              )
            )}
          </td>
        )}

        <td className={versionCellClass}>
          {isNotNil(version) ? (
            <div className="flex justify-center">
              <SyntheseCell
                version={version}
                visaEnRetard={isVersionVisaEnRetard(
                  version,
                  selectedDiffuseATeamIds,
                )}
                onOpenSynthese={onOpenSynthese}
                onOpenDestinataires={onOpenDestinataires}
                onOpenInterne={(versionId) => onOpenInterne(versionId, doc.id)}
              />
            </div>
          ) : (
            <span className="text-zinc-300">-</span>
          )}
        </td>
      </tr>
    );
  });
}

/**
 * A draft, a version still at the sas or one refused there is not a recorded
 * index. A rang 2 proposal keeps `status = propose` from its creation: only
 * `proposalSentAt` tells a draft from what reached the sas.
 */
function VersionStateLabel({ version }: { version: VersionData }) {
  if (version.status === "propose") {
    return isNotNil(version.proposalSentAt) ? (
      <span className="block text-[10px] leading-3 text-amber-700 dark:text-amber-400">
        Proposé
      </span>
    ) : (
      <span className="block text-[10px] leading-3 text-zinc-500 dark:text-zinc-400">
        Brouillon
      </span>
    );
  }
  if (version.status === "refuse") {
    return (
      <span
        className="block text-[10px] leading-3 text-red-600 dark:text-red-400"
        title={version.rejectionReason ?? undefined}
      >
        Refusé
      </span>
    );
  }
  return version.awaitingDiffusion ? <AwaitingDiffusionLabel /> : null;
}

function DocumentTextCell({
  rowSpan,
  className,
  value,
}: {
  rowSpan: number;
  className: string;
  value: string;
}) {
  return (
    <td rowSpan={rowSpan} className={cn("px-2 py-2 align-top", className)}>
      <div
        className={cn("truncate", stickyRowHeaderContentClass)}
        title={value}
      >
        {value}
      </div>
    </td>
  );
}

function maquetteLabel(
  maquette: Pick<MaquetteData, "number" | "name">,
): string {
  return isNil(maquette.number)
    ? maquette.name
    : `${maquette.number} - ${maquette.name}`;
}

/** Title of the viewer: two versions of a maquette can look the same. */
export function maquetteVersionLabel(
  maquette: Pick<MaquetteData, "number" | "name" | "index">,
): string {
  return isNil(maquette.index)
    ? maquetteLabel(maquette)
    : `${maquetteLabel(maquette)} - Version ${maquette.index}`;
}
